import { useEffect, useState } from "react";
import { staffApi, apiError } from "../lib/api";

export default function ModulesPage() {
  const [mods, setMods] = useState(null);
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pitchError, setPitchError] = useState("");
  const saveConcertPitch = async (v) => {
    if (!(v >= 430 && v <= 450)) {
      setPitchError("Choose a value between 430 and 450 Hz.");
      return;
    }
    try {
      await staffApi.put("/staff/settings", { concert_pitch: v });
      setSettings((s) => ({ ...s, concert_pitch: v }));
      setPitchError("");
    } catch {
      setPitchError("Only choir directors can change the concert pitch.");
    }
  };
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  const load = () => {
    staffApi.get("/staff/modules").then((r) => setMods(r.data.modules)).catch((e) => setError(apiError(e)));
    staffApi.get("/staff/groups").then((r) => setGroups(r.data.groups));
    staffApi.get("/staff/settings").then((r) => setSettings(r.data.settings)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (m, patch) => {
    setError(""); setSaved("");
    try {
      await staffApi.put("/staff/modules", {
        module_id: m.id, enabled: patch.enabled ?? m.enabled,
        scope: patch.scope ?? m.scope, group_ids: patch.group_ids ?? m.group_ids,
      });
      setSaved(m.id);
      setTimeout(() => setSaved(""), 1500);
      load();
    } catch (e) { setError(apiError(e)); }
  };

  const toggleGroup = (m, gid) => {
    const gids = m.group_ids.includes(gid) ? m.group_ids.filter((g) => g !== gid) : [...m.group_ids, gid];
    save(m, { group_ids: gids });
  };

  if (!mods) return <p className="text-[#566270]" data-testid="modules-loading">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="modules-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Learning Modules</h1>
        <p className="text-sm text-[#566270] mt-1">Choose which learning areas your choristers see. Disabled modules disappear completely from the child app.</p>
      </div>
      {error && <p role="alert" className="text-sm text-[#A62B2B]" data-testid="modules-error">{error}</p>}
      <div className="space-y-4">
        {mods.map((m) => (
          <section key={m.id} className="rounded-2xl bg-white border border-[#E5DEC9] p-5 sm:p-6" data-testid={`module-config-${m.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-bold text-[#0B192C] flex items-center gap-2">
                  {m.name}
                  {m.liturgical && <span className="text-[10px] font-bold uppercase tracking-wider text-[#7A1C2E] bg-[#FBF0F2] px-2 py-0.5 rounded-full">Optional · Liturgical</span>}
                  {saved === m.id && <span className="text-xs text-[#2D6A4F] font-bold">Saved</span>}
                </h2>
                <p className="text-sm text-[#566270] mt-0.5">{m.description}</p>
              </div>
              <button onClick={() => save(m, { enabled: !m.enabled })} data-testid={`module-enable-${m.id}`} role="switch" aria-checked={m.enabled}
                className={`px-5 py-3 rounded-xl font-bold text-sm transition-colors min-h-[48px] ${m.enabled ? "bg-[#2D6A4F] text-white" : "bg-[#F3EFE0] text-[#566270]"}`}>
                {m.enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
            {m.enabled && (
              <div className="mt-4 border-t border-[#F3EFE0] pt-4">
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => save(m, { scope: "all" })} data-testid={`scope-all-${m.id}`}
                    className={`px-4 py-2.5 rounded-full text-xs font-bold min-h-[44px] ${m.scope === "all" ? "bg-[#0B192C] text-[#FAF7EE]" : "bg-[#F3EFE0] text-[#566270]"}`}>
                    Whole institution
                  </button>
                  <button onClick={() => save(m, { scope: "groups" })} data-testid={`scope-groups-${m.id}`}
                    className={`px-4 py-2.5 rounded-full text-xs font-bold min-h-[44px] ${m.scope === "groups" ? "bg-[#0B192C] text-[#FAF7EE]" : "bg-[#F3EFE0] text-[#566270]"}`}>
                    Selected groups
                  </button>
                </div>
                {m.scope === "groups" && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {groups.map((g) => (
                      <button key={g.id} onClick={() => toggleGroup(m, g.id)} data-testid={`module-group-${m.id}-${g.id}`}
                        className={`px-4 py-2.5 rounded-full text-xs font-bold min-h-[44px] transition-colors ${m.group_ids.includes(g.id) ? "bg-[#C69B3D] text-[#0B192C]" : "bg-white border border-[#E5DEC9] text-[#566270]"}`}>
                        {m.group_ids.includes(g.id) ? "✓ " : ""}{g.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        ))}
      </div>
      {settings !== null && (
        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-5 sm:p-6" data-testid="gamification-settings">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Institution settings</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {[
              ["solfa", "Sight-singing solfa (doh-re-mi) questions"],
              ["streaks", "Practice streaks"],
              ["achievements", "Achievements"],
              ["coop_goals", "Cooperative choir goals"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-3 rounded-xl border border-[#F3EFE0] px-4 py-3 min-h-[52px]" data-testid={`setting-${key}`}>
                <span className="text-sm font-semibold text-[#0B192C]">{label}</span>
                <input type="checkbox" checked={!!settings[key]} aria-label={label}
                  onChange={async (e) => {
                    await staffApi.put("/staff/settings", { [key]: e.target.checked });
                    setSettings({ ...settings, [key]: e.target.checked });
                  }}
                  className="w-6 h-6 accent-[#C69B3D]" />
              </label>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-[#F3EFE0] px-4 py-3" data-testid="setting-concert-pitch">
            <p className="text-sm font-semibold text-[#0B192C]">Concert pitch — reference A4 (Hz)</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {[440, 441, 442].map((v) => (
                <button key={v} type="button" data-testid={`concert-pitch-${v}`} onClick={() => saveConcertPitch(v)}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-bold min-h-[44px] transition-colors ${
                    (settings.concert_pitch ?? 440) === v ? "border-[#C69B3D] bg-[#F6EED8] text-[#0B192C]"
                      : "border-[#E5DEC9] bg-white text-[#566270] hover:border-[#C69B3D]"}`}>{v} Hz</button>
              ))}
              <input type="number" min="430" max="450" step="0.5" aria-label="Custom concert pitch in Hz"
                value={settings.concert_pitch ?? 440}
                onChange={(e) => setSettings({ ...settings, concert_pitch: parseFloat(e.target.value) })}
                onBlur={(e) => saveConcertPitch(parseFloat(e.target.value))}
                className="w-24 rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm min-h-[44px]"
                data-testid="concert-pitch-input" />
              <span className="text-xs text-[#566270]">430–450 Hz · directors only · used by the Pitch Trainer for every chorister</span>
            </div>
            {pitchError && <p className="text-xs font-semibold text-[#A62B2B] mt-1.5" role="alert" data-testid="concert-pitch-error">{pitchError}</p>}
          </div>
        </section>
      )}
    </div>
  );
}
